'use client';

import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import React, { FC } from 'react';
import EmblaSlider from '~/components/embla-slider';
import EmblaContainer from '~/components/embla-slider/container';
import SliderNavigation from '~/components/embla-slider/navigation';
import EmblaSlide from '~/components/embla-slider/slide';
import Media from '~/data/strapi/types/defaults/media';
import alt from '~/utils/default-alt';
import getDataPath from '~/utils/get-data-path';

import styles from '../styles.module.css';

interface Props {
  images: Media[];
}

const ArticleImages: FC<Props> = ({ images }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', slidesToScroll: 1, containScroll: 'trimSnaps' });

  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  if (images.length === 0) return null;

  if (images.length === 1) {
    const image = images[0];
    return (
      <div className={styles.singleImageContainer}>
        <Image src={getDataPath(image.url)} alt={alt(image.alternativeText)} fill sizes="100vw" />
      </div>
    );
  }

  if (images.length === 2) {
    return (
      <>
        <div className={styles.doubleImageContainer}>
          {images.map((image) => (
            <div key={image.id} className={styles.doubleImageItem}>
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <Image src={getDataPath(image.url)} alt={alt(image.alternativeText)} fill sizes="50vw" />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.hideOnDesktop}>
          <div className={styles.slidersContainer}>
            <EmblaSlider ref={emblaRef}>
              <EmblaContainer className={styles.sliderContainer}>
                {images.map((image) => (
                  <EmblaSlide key={image.id} className={styles.slide}>
                    <div className={styles.slideImage}>
                      <Image src={getDataPath(image.url)} alt={alt(image.alternativeText)} fill sizes="100vw" />
                    </div>
                  </EmblaSlide>
                ))}
              </EmblaContainer>
            </EmblaSlider>
            <SliderNavigation onPrev={scrollPrev} onNext={scrollNext} containerClassName={styles.sliderNavigation} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={styles.slidersContainer}>
      <EmblaSlider ref={emblaRef}>
        <EmblaContainer className={styles.sliderContainer}>
          {images.map((image, index) => (
            <EmblaSlide key={index} className={styles.slide}>
              <div className={styles.slideImage}>
                <Image
                  src={getDataPath(image.url)}
                  alt={alt(image.alternativeText)}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaSlider>
      <SliderNavigation onPrev={scrollPrev} onNext={scrollNext} containerClassName={styles.sliderNavigation} />
    </div>
  );
};

export default ArticleImages;
